import React, { useEffect, useRef, useState } from 'react';
import { Search, Bell, Menu, X } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { useToast } from "../../components/ui/hooks/use-toast";
import { Button } from '@/components/ui/button';
import { HelpCircle, Play } from 'lucide-react';


type HeaderProps = {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
  handleStartTooltips: () => void;
  handleStartOnboarding: () => void;
};

const Header = ({ onToggleSidebar, isSidebarOpen, handleStartTooltips, handleStartOnboarding }: HeaderProps) => {
  const notifications = useStore((state) => state.notifications || []);
  const markNotificationAsRead = useStore((state) => state.markNotificationAsRead);
  const recipes = useStore((state) => state.recipes || []);
  const [searchResults, setSearchResults] = useState<Array<{ id: string; title: string; type: string }>>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const showNotificationsRef = useRef(showNotifications);
  // Keep track of previous number of notifications
  const prevCountRef = useRef(notifications.length);

  const { toast } = useToast();

  useEffect(() => {
    showNotificationsRef.current = showNotifications;
  }, [showNotifications]);

  useEffect(() => {
    const handleClickAnywhere = async (event: MouseEvent) => {
      // console.log('Clicked somewhere on the page:', event);
      setShowNotifications(false);
      await Promise.all(notifications.map((notification) => {
        markNotificationAsRead(notification.id);
      }));
    };

    let timeoutId: number;

    if (showNotifications) {
      // Delay to avoid closing immediately after opening
      timeoutId = window.setTimeout(() => {
        window.addEventListener('click', handleClickAnywhere);
      }, 0);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleClickAnywhere);
    };
  }, [showNotifications]);

  useEffect(() => {
    const currentCount = notifications.length;
    const prevCount = prevCountRef.current;

    // Trigger toast ONLY if a new notification was added
    if (currentCount > prevCount) {
      const latestNotification = notifications[0]; // assuming newest is first
      if (latestNotification) {
        toast({
          title: latestNotification.title,
          description: latestNotification.message,
        });
      }
    }

    // Update ref for next comparison
    prevCountRef.current = currentCount;
  }, [notifications, toast]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = recipes
      .filter(recipe => recipe.title.toLowerCase().includes(term))
      .map(recipe => ({ id: recipe.id, title: recipe.title, type: 'Recipe' }));

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleSearchResultClick = (result: { id: string; type: string }) => {
    // Clear search
    setSearchTerm('');
    setShowSearchResults(false);

    // Navigate based on result type
    if (result.type === 'Recipe') {
      // Find the recipe and trigger modal
      const recipe = recipes.find(r => r.id === result.id);
      if (recipe) {
        window.dispatchEvent(new CustomEvent('openRecipe', { detail: recipe }));
      }
    }
  };

  return (
    <header className="bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 py-4 px-6 flex items-center justify-between">
      <button
        onClick={onToggleSidebar}
        className="lg:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
      >
        {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <div className="relative max-w-xl w-full hidden lg:block">
        <input
          type="text"
          placeholder="Search recipes, plans, or settings..."
          value={searchTerm}
          onChange={handleSearch}
          onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
          onFocus={(e) => e.target.value.length >= 2 && setShowSearchResults(true)}
          className="w-full py-2 px-4 pl-10 pr-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-black text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />

        {showSearchResults && searchResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-2">
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  onClick={() => handleSearchResultClick(result)}
                  className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-white">{result.title}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{result.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className='flex gap-2'>
        <Button
          onClick={handleStartTooltips}
          variant="outline"
          size="sm"
          className="bg-black border-secondary-700 text-secondary-500 hover:bg-gray-900"
        >
          <HelpCircle className="w-4 h-4 mr-1" />
          Quick Tour
        </Button>
        <Button
          onClick={handleStartOnboarding}
          variant="outline"
          size="sm"
          className="bg-black border-secondary-700 text-secondary-500 hover:bg-gray-900"
        >
          <Play className="w-4 h-4 mr-1" />
          Onboarding
        </Button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
          >
            <Bell size={22} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#EA580C] text-white text-xs rounded-full h-4 w-4 p-1.5 flex items-center justify-center">
                {/* {unreadCount > 9? "9+" : unreadCount} */}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-black rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-800 dark:text-white">Notifications</h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 border-b border-gray-200 dark:border-gray-700 ${!notification.read ? 'bg-secondary/5 dark:bg-secondary/10' : ''
                        }`}
                      onClick={() => markNotificationAsRead(notification.id)}
                    >
                      <div className="flex items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-800 dark:text-white">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(notification.date).toLocaleDateString()}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-secondary rounded-full" />
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No notifications
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header